import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import MuiSelect from '@mui/material/Select';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import { useWidthRef } from '../../util/useWidthRef';
import { StyledMenuItem } from '../ButtonSelect';
import { ButtonSelectProps } from '../ButtonSelect/ButtonSelect';
import Input, { InputProps } from '../Input/Input';

const StyledSelect = styled(MuiSelect)`
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  & .MuiSelect-select {
    padding: 0 !important;
  }

  &.Mui-disabled .MuiSelect-nativeInput {
    display: none;
  }
`;

// Standalone StyledInputSelect component for custom use cases
export const StyledInputSelect = React.forwardRef<HTMLInputElement, InputProps>(
  function StyledInputSelect(props, ref) {
    const { children, ...inputProps } = props;
    return (
      <Input
        {...inputProps}
        value={children}
        ref={ref}
        endAdornment={inputProps.endAdornment || <UnfoldMoreRoundedIcon />}
      />
    );
  }
);

export type InputSelectProps<Value extends {} = object> = Omit<
  ButtonSelectProps<Value>,
  'onChange' | 'customTrigger'
> &
  Omit<InputProps, 'onChange' | 'value' | 'defaultValue'> & {
    options?: { text: string; value: Value }[];
    onChange?: (value: Value | null) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  };

export default React.forwardRef(function InputSelect<Value extends {}>(
  props: InputSelectProps<Value>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const internalRef = useRef<HTMLDivElement>(null);
  const widthModal = useWidthRef(internalRef);

  const {
    // ButtonSelect props
    value,
    defaultValue,
    onChange,
    onBlur,
    options,
    onClickItem,
    startListItem,
    listboxWidth,
    // InputProps
    label,
    error,
    helperText,
    fullWidth,
    slots,
    slotProps,
    classes,
    sx,
    sxInput,
    ownerState,
    InputProps,
    inputProps,
    placeholder,
    required,
    id,
    inputRef,
    endAdornment,
    disabled,
    ...otherProps
  } = props as any;

  // Find the selected option to display
  const selectedOption = options?.find((opt: any) => opt.value === value);
  const displayValue = selectedOption?.text ?? '';

  const handleChange = (event: any) => {
    const newValue = event.target.value;

    if (onChange) {
      onChange(newValue);
    }

    if (onClickItem) {
      onClickItem(newValue);
    }
  };

  return (
    <StyledSelect
      ref={ref || internalRef}
      id={id}
      name={otherProps.name}
      value={value ?? ''}
      onChange={handleChange}
      onBlur={onBlur}
      displayEmpty
      inputRef={inputRef}
      fullWidth={fullWidth}
      sx={sx}
      IconComponent={() => null}
      disabled={disabled}
      renderValue={() => (
        <Input
          id={id}
          name={otherProps.name}
          label={label}
          error={error}
          helperText={helperText}
          fullWidth
          slots={slots}
          slotProps={slotProps}
          classes={classes}
          sxInput={sxInput}
          ownerState={ownerState}
          InputProps={InputProps}
          inputProps={inputProps}
          placeholder={placeholder}
          required={required}
          value={displayValue}
          endAdornment={endAdornment || <UnfoldMoreRoundedIcon />}
          disabled={disabled}
          readOnly
        />
      )}
      MenuProps={{
        PaperProps: {
          sx: {
            ...(widthModal && {
              width: widthModal,
              maxWidth: widthModal,
            }),
          },
        },
      }}
      {...otherProps}
    >
      {startListItem}
      {options?.map((option: any) => (
        <StyledMenuItem
          key={JSON.stringify(option.value)}
          value={option.value as any}
        >
          {option.text}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );
});
